import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type NewProperty = components['schemas']['CreatePropertyRequest'];
export type NewJob = components['schemas']['CreateJobRequest'];
export type NewCustomer = components['schemas']['CreateCustomerRequest'];
export type CustomerEdits = components['schemas']['UpdateCustomerRequest'];

/**
 * Mutations for the customer book: add a property and start a job (M1-04),
 * and create/edit/archive a customer itself (M1-30).
 *
 * <b>Not optimistic</b>, unlike the dispatch board's move. A move is a small,
 * frequent, reversible gesture where waiting on a round trip is the cost;
 * creating or editing a record is none of those. Showing a customer or a
 * property the server then refuses would mean inventing an id for a row that
 * does not exist, and taking it away again is a far worse surprise than a
 * spinner on a button pressed once.
 *
 * <b>The third quick action M1-04's AC asked for — new estimate — is not
 * here.</b> There is no estimate model yet: it is M2-05, on the shared
 * line-item model in M2-04. A button that cannot do anything is worse than an
 * absent one, so it is absent and the AC line is carried to M2 with the
 * amount column.
 */

export function useAddProperty(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (property: Omit<NewProperty, 'customerId'>) => {
      const { data, response } = await apiClient.POST('/api/v1/properties', {
        body: { ...property, customerId },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/properties failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', 'byCustomer', customerId] });
      // The customer list is invalidated too: M1-03 searches property
      // addresses, so a new address changes which terms find this customer.
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
    },
  });
}

/**
 * Add a customer, from the list screen (M1-30).
 *
 * Returns the full `CreateCustomerResponse` rather than unwrapping it: the
 * caller needs `duplicateNameWarning` too, and M1-01 made that a warning, not
 * a block — the customer is created either way, so this never rejects on it.
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customer: NewCustomer) => {
      const { data, response } = await apiClient.POST('/api/v1/customers', {
        body: customer,
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/customers failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
    },
  });
}

/**
 * Edit a customer, from the detail page.
 *
 * `Type` is absent from `UpdateCustomerRequest` on the API side, not just
 * omitted from this form — M1-01 documented why: switching it means
 * recreating the underlying Party as the other subtype and re-pointing every
 * PropertyParty, PartyRelationship and Job that references it. A customer
 * that must change kind is archived and recreated, not edited.
 */
export function useUpdateCustomer(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (edits: CustomerEdits) => {
      const { data, response } = await apiClient.PUT('/api/v1/customers/{id}', {
        params: { path: { id: customerId } },
        body: edits,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/customers/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
      // M1-03 searches name, phone, email and property address, all of which
      // this form can change — a stale search result pointing at the old name
      // would be a worse bug than an extra refetch.
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      // M2-16: every invoice names the address Send and Resend would use
      // (`sendTo`, the customer's email else the payer person's) in its
      // confirm, and Send skips the confirm when there is none. A cached
      // invoice left fresh across this edit would name the old address — or,
      // after "no email on file", send with no confirm at all.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // M2-21, the same trap on the job page: each job names the address its
      // appointment emails go to, and whether "on my way" can be sent at all
      // ("…no email address on file"). Every job query, so a job page opened
      // inside the staleTime does not keep refusing after the email is added.
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * Archive a customer, from the detail page.
 *
 * Idempotent on the API side (M1-01) -- archiving an already-archived customer
 * just confirms it -- so this never needs to guard against double-clicking.
 */
export function useArchiveCustomer(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, response } = await apiClient.POST('/api/v1/customers/{id}/archive', {
        params: { path: { id: customerId } },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/customers/{id}/archive failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
    },
  });
}

export function useCreateJob(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (job: Omit<NewJob, 'customerId'>) => {
      const { data, response } = await apiClient.POST('/api/v1/jobs', {
        body: { ...job, customerId },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/jobs failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs', 'byCustomer', customerId] });
      // Every dispatch board window, not one of them: a job created here with
      // a time belongs on whichever day that is, and this screen does not know
      // which windows the board has cached.
      void queryClient.invalidateQueries({ queryKey: ['jobs', 'board'] });
    },
  });
}

/**
 * Set or clear how this customer found the shop (M1-45).
 *
 * ⚠️ <b>Its own route, and it must stay that way.</b> Lead source WAS a field on
 * `UpdateCustomerRequest`, and that was a data-loss bug: a positional record
 * cannot tell an omitted field from an explicit null, so every caller that did
 * not send it — which was every caller, the shipped Edit dialog included —
 * silently erased the attribution on an unrelated edit. On a route that does
 * nothing else, null unambiguously means "clear it". Do not fold this back into
 * `useUpdateCustomer`, however tempting the one-fewer-request looks.
 */
export function useSetCustomerLeadSource(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lead: { leadSourceId: string | null; leadSourceDetail: string | null }) => {
      const { data, response } = await apiClient.PUT('/api/v1/customers/{id}/lead-source', {
        params: { path: { id: customerId } },
        body: lead,
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/customers/{id}/lead-source failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => {
      // This record only. Attribution is not searched (M1-03 searches name,
      // phone, email and property address), and the catalog's own rename path
      // is what invalidates `customers` wholesale when a LABEL changes.
      void queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
    },
  });
}
